import React, { Component } from 'react';
import _ from 'lodash';
import { Grid } from 'semantic-grab-ui';
import './MetricsStatsTable.scss';
import moment from 'moment';
import {
  getPercentage,
  isPushChannel,
  printNumber,
} from 'omega-rtc/views/trident-campaigns/metrics/utils';
import MetricsStatsRow from 'omega-rtc/views/trident-campaigns/metrics/MetricsStatsRow';
import { commsChannels } from 'omega-rtc/data/trident-campaigns';
import { METRICS_FIELD_MAPPING, METRICS_RATES, METRICS_TO_RATE_NAME_MAPPING } from 'omega-rtc/constants/ucd';
import { BenchmarkMap, CommsMetricsMap } from 'omega-rtc/types/trident-campaigns';
import type { CommsMetrics } from 'omega-rtc/types/trident-campaigns';
import { SCORE_CATEGORY } from 'omega-rtc/constants/metrics';
import NotSentDetailsModal from './NotSentDetailsModal';
import { Button, Icon } from 'grab-ui';

const DATE_FORMAT = 'DD MMM YYYY. hh:mma';
const CONTROL_GROUP_KEY_PREFIX = 'Control Group';
const METRICS = METRICS_FIELD_MAPPING.default;

type Props = {
  metricsStat: CommsMetricsMap, // Map of group / channel name to metrics. {"Control Group (10%)": {attempted:620, click:100, ...}, ...}
  lastUpdatedDate: string, // 2023-01-11T14:27:36.528740764Z
  headerTitle: string, // the text displayed on the most top left column
  selectedChannel: string, // selected channel from the dropdown
  metricsBenchmark?: BenchmarkMap,
  notSentMetrics: any[], // Array of not-sent metrics data
  onFetchNotSentMetrics: () => void, // Function to fetch not-sent metrics on demand
  isLoadingNotSentMetrics: boolean, // Loading state for not-sent metrics
  nodeType?: string, // The type of node (schedule, scenarios, etc.)
  droppedByXCO?: number, // Campaign-level XCO drop count, same value shown for all groups/channels
};

interface MetricsStatsTableState {
  showNotSentModal: boolean;
  hasFetchedNotSentMetrics: boolean;
}

class MetricsStatsTable extends Component<Props, MetricsStatsTableState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showNotSentModal: false,
      hasFetchedNotSentMetrics: false,
    };
  }

  handleViewDetailsClick = () => {
    if (!this.state.hasFetchedNotSentMetrics && this.props.onFetchNotSentMetrics) {
      this.props.onFetchNotSentMetrics();
      this.setState({ hasFetchedNotSentMetrics: true });
    }
    this.setState({ showNotSentModal: true });
  }

  constructBreakdownGroupTitleRow = (title: string) => {
    return (<Grid.Row>
      <Grid.Column className={'stats-breakdown-group-title'}>
        {title}
      </Grid.Column>
    </Grid.Row>);
  }

  hasExperimentGroup(metricsKeys: string[]) {
    return _.some(metricsKeys, key => key.startsWith(CONTROL_GROUP_KEY_PREFIX));
  }

  findControlGroupName(controlGroups: string[]) {
    return _.find(controlGroups, controlGroup => controlGroup.startsWith(CONTROL_GROUP_KEY_PREFIX));
  }


  getBenchmarkByChannel(metricsBenchmark: BenchmarkMap, channel: string, rateType: string, channelRate: number) {
    if (_.isEmpty(metricsBenchmark)) {
      return { benchmarkScore: '', benchmarkRate: 0 };
    }
    const benchmarkPercentile90Rate = _.get(metricsBenchmark, `percentile90.${channel}.${rateType}`, 0);
    const benchmarkAverageRate = _.get(metricsBenchmark, `average.${channel}.${rateType}`, 0);

    if (channelRate > benchmarkPercentile90Rate) {
      return { benchmarkScore: SCORE_CATEGORY.great, benchmarkRate: benchmarkPercentile90Rate };
    } else if (channelRate > benchmarkAverageRate) {
      return { benchmarkScore: SCORE_CATEGORY.good, benchmarkRate: benchmarkPercentile90Rate };
    }
    return { benchmarkScore: SCORE_CATEGORY.bad, benchmarkRate: benchmarkAverageRate };
  }

  // Return sample: {val: 700, percentage: 85.5, uplift: 3.15}
  constructItem(metricsObj: CommsMetrics, valueField: string, dividerField: string, conGroupRate: number,
    metricsBenchmark?: BenchmarkMap,
    key?: string): any {
    const value = _.get(metricsObj, valueField, 0);
    const rate = getPercentage(value, _.get(metricsObj, dividerField, 0));

    const result: any = {
      val: value,
      percentage: rate,
      uplift: rate - conGroupRate,
    };

    if (metricsBenchmark) {
      const {
        benchmarkScore,
        benchmarkRate,
      } = this.getBenchmarkByChannel(metricsBenchmark, key, METRICS_TO_RATE_NAME_MAPPING[valueField], rate);
      result.uplift = rate - benchmarkRate;
      result.benchmarkScore = benchmarkScore;
      result.benchmarkRate = benchmarkRate;
    }

    return result;
  }

  // Return sample {val: 700}
  constructItemsVal(metricsStat: CommsMetricsMap, field: string, selectedChannel?: string) {
    const metricsKeys = Object.keys(metricsStat);

    return metricsKeys.map(key => {
      const isDeliveredOrUndelivered = field === METRICS.delivered || field === METRICS.undelivered;
      const isEmailChannel = key === commsChannels.email || selectedChannel === commsChannels.email;
      const shouldHide = isDeliveredOrUndelivered && !isEmailChannel;

      const fieldValue = _.get(metricsStat, `${key}.${field}`, 0);

      return {
        hide: shouldHide,
        val: fieldValue,
      };
    });
  }


  render() {
    const { metricsStat, lastUpdatedDate, headerTitle, selectedChannel, metricsBenchmark } = this.props;
    const metricsKeys = _.keys(metricsStat); // In holos page. MetricsKeys are the 'channels'  e.g: Email, Push. Meanwhile, in trident it will be 'Group name'
    const columnWidth = Math.floor(16 / (metricsKeys.length + 1));

    const totalTriggeredStat = this.getTotalTriggered(metricsStat);
    const hasExperimentGroup = this.hasExperimentGroup(metricsKeys);
    const hasBenchmark = !_.isEmpty(metricsBenchmark);

    let conGrpKey;
    let conGrpSuccessfulSentRate = 0;
    let conGrpDeliverabilityRate = 0;
    let conGrpOpenRate = 0;
    let conGrpClickRate = 0;

    if (hasExperimentGroup) {
      conGrpKey = this.findControlGroupName(metricsKeys);
      const defaultStats = {
        sent: 0,
        notSent: 0,
        delivered: 0,
        open: 0,
        click: 0,
      };
      const conGrpStats = _.assign(defaultStats, _.get(metricsStat, conGrpKey));

      conGrpSuccessfulSentRate = getPercentage(conGrpStats.sent, conGrpStats.sent + conGrpStats.notSent);
      conGrpDeliverabilityRate = getPercentage(conGrpStats.delivered, conGrpStats.sent);
      conGrpOpenRate = getPercentage(
        conGrpStats.open,
        isPushChannel(selectedChannel) ? conGrpStats.sent : conGrpStats.delivered
      );
      conGrpClickRate = getPercentage(conGrpStats.click, conGrpStats.open);
    }


    // Adding additional fields to metrics stats. e.g: {..., attempted: 1200, undelivered: 500, rates: { sent: {val: 700, percentage: 85.5, uplift: 3.15}, deliverability: ....}
    const appendedMetricsStat = _.mapValues(metricsStat, (stat, key) => {
      const appendedStat = {
        ...stat,
        attempted: _.get(stat, METRICS.sent, 0) + _.get(stat, METRICS.notSent, 0),
        undelivered: _.get(stat, METRICS.sent, 0) - _.get(stat, METRICS.delivered, 0),
      };

      const isPush = isPushChannel(key) || isPushChannel(selectedChannel);
      const isEmail = (key === commsChannels.email || selectedChannel === commsChannels.email);

      return {
        ...appendedStat,
        rates: {
          sent: this.constructItem(appendedStat, METRICS.sent, METRICS.attempted, conGrpSuccessfulSentRate),
          deliverability: isEmail // Number of delivered is currently wrong (except email), therefore we need to hide it
            ? this.constructItem(appendedStat, METRICS.delivered, METRICS.sent, conGrpDeliverabilityRate)
            : { hide: true },
          open: this.constructItem(
            appendedStat,
            METRICS.open,
            isPush ? METRICS.sent : METRICS.delivered,
            conGrpOpenRate,
            metricsBenchmark,
            key
          ),
          click: isPush
            ? { hide: true }
            : this.constructItem(appendedStat, METRICS.click, METRICS.open, conGrpClickRate, metricsBenchmark, key),
        },
      };
    });


    // rate breakdown items
    const successfulSentRates = _.map(metricsKeys, key => _.get(appendedMetricsStat, `${key}.${METRICS_RATES.sent}`, {}));
    const deliveryRates = _.map(metricsKeys, key => _.get(appendedMetricsStat, `${key}.${METRICS_RATES.deliverability}`, {}));
    const openRates = _.map(metricsKeys, key => _.get(appendedMetricsStat, `${key}.${METRICS_RATES.open}`, {}));
    const clickRates = _.map(metricsKeys, key => _.get(appendedMetricsStat, `${key}.${METRICS_RATES.click}`, {}));


    // detailed breakdown items
    const triggeredItems = _.map(metricsKeys, key => {
      const triggered = _.get(appendedMetricsStat, `${key}.${METRICS.triggered}`, 0);
      return {
        val: triggered,
        percentage: getPercentage(triggered, totalTriggeredStat),
      };
    });

    const sentItems = this.constructItemsVal(appendedMetricsStat, METRICS.sent);
    const notSentItems = this.constructItemsVal(appendedMetricsStat, METRICS.notSent);
    const deliveredItems = this.constructItemsVal(appendedMetricsStat, METRICS.delivered, selectedChannel);
    const undeliveredItems = this.constructItemsVal(appendedMetricsStat, METRICS.undelivered, selectedChannel);
    const openItems = this.constructItemsVal(appendedMetricsStat, METRICS.open);
    const clickItems = _.map(metricsKeys, key => ({
      val: _.get(appendedMetricsStat, `${key}.${METRICS.click}`, 0),
      hide: isPushChannel(key) || isPushChannel(selectedChannel),
    }));
    const droppedByXCOItems = this.props.droppedByXCO != null
      ? metricsKeys.map(() => ({ val: this.props.droppedByXCO }))
      : null;

    return (
      <div style={{ paddingTop: 30 }} className={'metrics-stat-table'}>
        <Grid>
          <Grid.Row className={'header'}>
            <Grid.Column width={columnWidth} style={{ textAlign: 'left' }}>
              {headerTitle} ({metricsKeys.length})
            </Grid.Column>
            {_.map(metricsKeys, key => {
              return (<Grid.Column key={key} width={columnWidth}>
                {key}
              </Grid.Column>);
            })
            }
          </Grid.Row>

          {this.constructBreakdownGroupTitleRow('Rate Breakdown')}
          <MetricsStatsRow
            title="Successful Sent Rate" items={successfulSentRates} width={columnWidth}
            showUplift={hasExperimentGroup}
            tooltip={'Number of successfully sent as percentage of total send attempt'}
          />
          <MetricsStatsRow
            title="Deliverability Rate" items={deliveryRates} width={columnWidth}
            showUplift={hasExperimentGroup}
            tooltip={'Number of messages delivered as percentage of Segment Size. This metric is only available for EDM channel type.'}
          />
          <MetricsStatsRow
            title="Open Rate" items={openRates} width={columnWidth}
            showUplift={hasExperimentGroup || hasBenchmark}
            tooltip={'Open Rates are derived as such: Email = Open / Delivered. Push, PCI & Inbox = Open / Sent Success'}
          />
          <MetricsStatsRow
            title="Click through rate" items={clickRates} width={columnWidth}
            showUplift={hasExperimentGroup || hasBenchmark}
          />

          {this.constructBreakdownGroupTitleRow('Detailed Breakdown')}
          <MetricsStatsRow
            title="Triggered" items={triggeredItems} width={columnWidth}
            tooltip={'Total triggered count from trident. Failure action is also counted'}
          />
          <MetricsStatsRow title="Sent" items={sentItems} width={columnWidth} />
          {this.props.nodeType === 'schedule' ? (
            <Grid.Row>
              <Grid.Column width={columnWidth}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginLeft: 2 }}>Not Sent</span>
                  <Button
                    type="link"
                    size="small"
                    onClick={this.handleViewDetailsClick}
                    loading={this.props.isLoadingNotSentMetrics}
                    style={{
                      padding: '4px 8px',
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '13px',
                    }}
                  >
                    <Icon type="eye" style={{ fontSize: '14px' }} />
                    View Details
                  </Button>
                </div>
              </Grid.Column>
              {_.map(notSentItems, (item, index) => (
                <Grid.Column key={index} width={columnWidth}>
                  {!_.get(item, 'hide', false) && printNumber(_.get(item, 'val', 0))}
                </Grid.Column>
              ))}
            </Grid.Row>
          ) : (
            <MetricsStatsRow
              title="Not Sent"
              items={notSentItems}
              width={columnWidth}
              tooltip={'We check on user consent, frequency caps, etc. before sending your message.'}
            />
          )}
          <MetricsStatsRow title="Delivered" items={deliveredItems} width={columnWidth} />
          <MetricsStatsRow
            title="Undelivered" items={undeliveredItems} width={columnWidth}
            tooltip={'Push notification may be undelivered as user has logged out or uninstalled the app; Email may be undelivered due to invalid email address'}
          />
          <MetricsStatsRow title="Open" items={openItems} width={columnWidth} />
          <MetricsStatsRow title="Click" items={clickItems} width={columnWidth} />
          {droppedByXCOItems && (
            <MetricsStatsRow
              title="Filtered out by XCO" items={droppedByXCOItems} width={columnWidth}
              tooltip={
                'This is the number of messages that were filtered out at the pax level by Smart Comms XCO based on relevance scoring.'
              }
            />
          )}
          {!_.isEmpty(lastUpdatedDate) && (
            <p className={'date'}>Last Updated : {moment(lastUpdatedDate).format(DATE_FORMAT)}</p>
          )}
        </Grid>

        <NotSentDetailsModal
          visible={this.state.showNotSentModal}
          onClose={() => this.setState({ showNotSentModal: false })}
          notSentData={this.props.notSentMetrics}
          selectedChannel={selectedChannel}
          isLoading={this.props.isLoadingNotSentMetrics}
        />
      </div>);
  }

  getTotalTriggered = (metricsStats: CommsMetricsMap) => {
    let totalTriggered = 0;
    _.forEach(metricsStats, (metrics, key) => {
      if (key !== commsChannels.pciInbox) { // exclude PCI inbox as the count is already taken from PCI push
        totalTriggered += _.get(metrics, METRICS.triggered, 0);
      }
    });
    return totalTriggered;
  }
}

export default MetricsStatsTable;
